import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { IconButton, useTheme } from '@mui/material';
import Snackbar, { type SnackbarCloseReason } from '@mui/material/Snackbar';

import IconXSmall from '@/assets/icons/x-small.svg?react';

const DEFAULT_SNACK_MESSAGE_DURATION = 6000;

interface SnackbarContextType {
  showMessage: (message: string, duration?: number) => void;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType>({} as SnackbarContextType);

export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const { palette } = useTheme();

  const showMessage = useCallback((newMessage: string, newDuration?: number) => {
    setMessage(newMessage);
    setDuration(newDuration ?? DEFAULT_SNACK_MESSAGE_DURATION);
    setOpen(true);
  }, []);

  const onClose = useCallback((_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  const context = useMemo(
    () => ({
      showMessage
    }),
    [showMessage]
  );

  const action = (
    <IconButton disableRipple={true} onClick={onClose} sx={{ color: palette.background.default }}>
      <IconXSmall />
    </IconButton>
  );

  return (
    <SnackbarContext.Provider value={context}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={onClose}
        message={message}
        action={action}
        sx={{
          color: palette.background.default
        }}
      />
    </SnackbarContext.Provider>
  );
};
